"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import type { Announcement } from "@/lib/types"
import { apiStore } from "@/lib/api-store"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog"

interface AnnouncementCarouselProps {
    title?: string
    autoplayInterval?: number
    limit?: number
}

export function AnnouncementCarousel({
    title = "📣 Annonces",
    autoplayInterval = 4000,
    limit = 12,
}: AnnouncementCarouselProps) {
    const [announcements, setAnnouncements] = useState<Announcement[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [itemsPerView, setItemsPerView] = useState(3)
    const [isHovered, setIsHovered] = useState(false)
    const [loading, setLoading] = useState(true)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    const [selected, setSelected] = useState<Announcement | null>(null)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth < 640) {
                setItemsPerView(1)
            } else if (window.innerWidth < 1024) {
                setItemsPerView(2)
            } else {
                setItemsPerView(3)
            }
        }

        updateItemsPerView()
        window.addEventListener("resize", updateItemsPerView)
        return () => window.removeEventListener("resize", updateItemsPerView)
    }, [])

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true)
                const data = await apiStore.getPublishedAnnouncements({ limit })
                setAnnouncements(data || [])
            } catch (error) {
                console.error("Erreur lors du chargement des annonces:", error)
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [limit])

    useEffect(() => {
        if (!isHovered && announcements.length > itemsPerView) {
            startAutoplay()
        } else {
            stopAutoplay()
        }

        return () => stopAutoplay()
    }, [isHovered, announcements.length, currentIndex, itemsPerView, autoplayInterval])

    const startAutoplay = () => {
        stopAutoplay()
        intervalRef.current = setInterval(() => {
            setCurrentIndex((prev) => {
                const maxIndex = Math.max(0, announcements.length - itemsPerView)
                return prev >= maxIndex ? 0 : prev + 1
            })
        }, autoplayInterval)
    }

    const stopAutoplay = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    const goToNext = () => {
        const maxIndex = Math.max(0, announcements.length - itemsPerView)
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
    }

    const goToPrevious = () => {
        const maxIndex = Math.max(0, announcements.length - itemsPerView)
        setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1))
    }

    const openDetails = (a: Announcement) => {
        setSelected(a)
        setOpen(true)
    }

    if (loading) {
        return (
            <div className="py-10 bg-gradient-to-b from-primary/5 to-background">
                <div className="container">
                    <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                        {title}
                    </h2>
                    <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                    </div>
                </div>
            </div>
        )
    }

    if (!announcements || announcements.length === 0) {
        return null
    }

    return (
        <div className="py-10 bg-gradient-to-b from-primary/5 to-background">
            <div className="container">
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                    {title}
                </h2>

                <div
                    className="relative group"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {announcements.length > itemsPerView && (
                        <>
                            <button
                                onClick={goToPrevious}
                                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full p-2 sm:p-3 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                                aria-label="Précédent"
                            >
                                <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 bg-white dark:bg-gray-800 shadow-lg rounded-full p-2 sm:p-3 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                                aria-label="Suivant"
                            >
                                <ChevronRight size={20} className="sm:w-6 sm:h-6" />
                            </button>
                        </>
                    )}

                    <div className="overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-in-out gap-4 sm:gap-6"
                            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
                        >
                            {announcements.map((a) => (
                                <button
                                    key={a._id || a.id}
                                    type="button"
                                    onClick={() => openDetails(a)}
                                    className="group/announcement flex-shrink-0 text-left w-full sm:w-1/2 lg:w-1/3 cursor-pointer"
                                    style={{
                                        width: `calc(${100 / itemsPerView}% - ${((itemsPerView - 1) * (itemsPerView === 1 ? 16 : 24)) / itemsPerView
                                            }px)`,
                                    }}
                                >
                                    <div className="h-full rounded-xl border border-border dark:border-dark-border bg-background dark:bg-dark-background overflow-hidden transition will-change-transform group-hover/announcement:shadow-lg group-hover/announcement:-translate-y-0.5">
                                        {a.image ? (
                                            <div className="relative w-full h-40">
                                                <Image
                                                    src={a.image}
                                                    alt={a.title}
                                                    fill
                                                    className="object-cover"
                                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                                <div className="absolute bottom-3 left-3 right-3">
                                                    <p className="text-white font-semibold line-clamp-2">{a.title}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-full h-40 bg-gradient-to-br from-primary/20 to-purple-200/30 flex items-center justify-center">
                                                <p className="text-primary font-semibold px-4 text-center line-clamp-2">{a.title}</p>
                                            </div>
                                        )}

                                        <div className="p-4">
                                            <p className="text-sm text-foreground-secondary dark:text-dark-foreground-secondary line-clamp-3">
                                                {a.description}
                                            </p>
                                            <p className="mt-3 text-sm font-medium text-primary group-hover/announcement:underline">
                                                Voir les détails
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {announcements.length > itemsPerView && (
                        <div className="flex justify-center gap-2 mt-6">
                            {Array.from({ length: Math.max(1, announcements.length - itemsPerView + 1) }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`h-2 rounded-full transition-all ${currentIndex === index ? "w-8 bg-primary" : "w-2 bg-gray-300 hover:bg-gray-400"
                                        }`}
                                    aria-label={`Aller à la position ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-6xl min-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selected?.title}</DialogTitle>
                        <DialogDescription>
                            {selected?.publishedAt ? `Publié le ${new Date(selected.publishedAt).toLocaleDateString('fr-FR')}` : ""}
                        </DialogDescription>
                    </DialogHeader>

                    {selected?.image ? (
                        <div className="relative w-full h-[60vh] sm:h-80 min-h-[60vh] rounded-lg overflow-hidden border border-border dark:border-dark-border bg-muted/20">
                            <Image
                                src={selected.image}
                                alt={selected.title}
                                fill
                                className="object-contain"
                                sizes="(max-width: 768px) 100vw, 768px"
                            />
                        </div>
                    ) : null}

                    <div className="text-sm text-foreground dark:text-dark-foreground whitespace-pre-wrap">
                        {selected?.description}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
